/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Antigravity
 🏢 Company: Ring2All LLC
 📄 File: hooks/useQueueCallbackList.ts (Queue Callback Report)
 🗓️ Created: 2026-01-16
 🧩 Description:
    React hook for managing Queue Callback list state.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import {
    fetchCallbacks,
    fetchQueues,
    cancelCallback as apiCancelCallback,
    retryCallback as apiRetryCallback,
    deleteCallback as apiDeleteCallback
} from '../api/queueCallbackApi';
import { QueueCallback, CallbackFilters, QueueOption, CallbackStatus } from '../types';

export interface UseQueueCallbackListReturn {
    data: QueueCallback[];
    loading: boolean;
    error: string | null;
    queues: QueueOption[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    filters: CallbackFilters;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    searchQuery: string;
    handleSearch: (query: string) => void;
    handleSort: (column: string) => void;
    handlePageChange: (page: number) => void;
    handleItemsPerPageChange: (limit: number) => void;
    handleFilterChange: (key: keyof CallbackFilters, value: string) => void;
    handleClearFilters: () => void;
    handleApplyFilters: () => void;
    handleCancel: (id: number) => Promise<boolean>;
    handleRetry: (id: number) => Promise<boolean>;
    handleDelete: (id: number) => Promise<boolean>;
    refresh: () => void;
    columns: Array<{
        key: string;
        label: string;
        sortable: boolean;
        visible: boolean;
        width?: string;
    }>;
}

export function useQueueCallbackList(): UseQueueCallbackListReturn {
    const { t } = useI18n();

    const [data, setData] = useState<QueueCallback[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [queues, setQueues] = useState<QueueOption[]>([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 0
    });
    const [filters, setFilters] = useState<CallbackFilters>({});
    const [appliedFilters, setAppliedFilters] = useState<CallbackFilters>({});
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [searchQuery, setSearchQuery] = useState('');

    // Define columns
    const columns = useMemo(() => [
        { key: 'id', label: t('queueCallbackReport.columns.id'), sortable: true, visible: false, width: '60px' },
        { key: 'queueName', label: t('queueCallbackReport.columns.queueName'), sortable: true, visible: true, width: '150px' },
        { key: 'callerIdNumber', label: t('queueCallbackReport.columns.callerIdNumber'), sortable: true, visible: true, width: '130px' },
        { key: 'callbackNumberNorm', label: t('queueCallbackReport.columns.callbackNumber'), sortable: true, visible: true, width: '130px' },
        { key: 'status', label: t('queueCallbackReport.columns.status'), sortable: true, visible: true, width: '100px' },
        { key: 'attemptCount', label: t('queueCallbackReport.columns.attemptCount'), sortable: true, visible: true, width: '80px' },
        { key: 'nextAttemptAt', label: t('queueCallbackReport.columns.nextAttemptAt'), sortable: true, visible: true, width: '150px' },
        { key: 'createdAt', label: t('queueCallbackReport.columns.createdAt'), sortable: true, visible: true, width: '150px' },
        { key: 'lastHangupCause', label: t('queueCallbackReport.columns.lastHangupCause'), sortable: false, visible: false, width: '120px' },
        { key: 'actions', label: t('queueCallbackReport.columns.actions'), sortable: false, visible: true, width: '120px' },
    ], [t]);

    // Fetch queues for dropdown
    useEffect(() => {
        fetchQueues()
            .then(res => setQueues(res.data || []))
            .catch(() => setQueues([]));
    }, []);

    // Fetch data
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchCallbacks(
                pagination.page,
                pagination.limit,
                sortBy,
                sortOrder,
                { ...appliedFilters, search: searchQuery }
            );
            setData(result.data || []);
            setPagination(prev => ({
                ...prev,
                total: result.pagination.total,
                totalPages: result.pagination.totalPages
            }));
        } catch (err) {
            setError(t('queueCallbackReport.messages.loadError'));
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, sortBy, sortOrder, appliedFilters, searchQuery, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const handleSort = useCallback((column: string) => {
        if (column === sortBy) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [sortBy]);

    const handlePageChange = useCallback((page: number) => {
        setPagination(prev => ({ ...prev, page }));
    }, []);

    const handleItemsPerPageChange = useCallback((limit: number) => {
        setPagination(prev => ({ ...prev, limit, page: 1 }));
    }, []);

    const handleFilterChange = useCallback((key: keyof CallbackFilters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleClearFilters = useCallback(() => {
        setFilters({});
        setAppliedFilters({});
        setSearchQuery('');
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const handleApplyFilters = useCallback(() => {
        setAppliedFilters(filters);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [filters]);

    const handleCancel = useCallback(async (id: number): Promise<boolean> => {
        try {
            await apiCancelCallback(id);
            loadData();
            return true;
        } catch {
            return false;
        }
    }, [loadData]);

    const handleRetry = useCallback(async (id: number): Promise<boolean> => {
        try {
            await apiRetryCallback(id);
            loadData();
            return true;
        } catch {
            return false;
        }
    }, [loadData]);

    const handleDelete = useCallback(async (id: number): Promise<boolean> => {
        try {
            await apiDeleteCallback(id);
            loadData();
            return true;
        } catch {
            return false;
        }
    }, [loadData]);

    const refresh = useCallback(() => {
        loadData();
    }, [loadData]);

    return {
        data,
        loading,
        error,
        queues,
        pagination,
        filters,
        sortBy,
        sortOrder,
        searchQuery,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleFilterChange,
        handleClearFilters,
        handleApplyFilters,
        handleCancel,
        handleRetry,
        handleDelete,
        refresh,
        columns
    };
}
